"use client";
import React, { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { useSearchParams } from "next/navigation";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { FilterMenuIcon, SearchIcon } from "~/components/icons";
import useEventsStore from "~/stores/events-store";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTrigger,
} from "components/ui/sheet";
import { EventsFilters } from "../events-filters";

export const SearchBar = () => {
  const [input, setInput] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const router = useRouter();
  const searchRef = useRef<any | null>(null);
  const searchParams = useSearchParams();

  const setFilter = useEventsStore((state) => state.setFilter);

  useEffect(() => {
    const search = searchParams.get("search");
    const type = searchParams.get("type");
    if (search) {
      setInput(search);
      setFilter("searchQuery", search);
    }
    if (type) {
      setFilter("type", type);
    }
  }, [searchParams, setFilter]);

  const handleSearch = () => {
    if (input !== "") {
      router.push(`/events?search=${input}`);
      setFilter("searchQuery", input);
    } else {
      router.push(`/events`);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <>
      <h6 className="font-p-sm font-medium">Search by keyword</h6>
      <div
        className="z-50 flex w-full items-center justify-center gap-3 text-white"
        ref={searchRef}
      >
        <div className="self-center xl:hidden">
          <Sheet>
            <SheetTrigger className="rounded-md border border-black p-2">
              <div className="h-[40px] w-[40px] ">
                <FilterMenuIcon />
              </div>
            </SheetTrigger>
            <SheetContent side="left" className="z-[11111]">
              <SheetDescription className="pt-5">
                <EventsFilters />
              </SheetDescription>
            </SheetContent>
          </Sheet>
        </div>
        <div className="my-5 w-full ">
          <div className="z-50 w-full rounded-md bg-rainbow-diagonal p-[1px]">
            <div className="flex w-full items-center justify-between rounded-md bg-white px-3 py-3 text-dark-gray md:justify-around md:gap-1">
              <Button
                className="flex self-center hover:bg-opacity-10"
                variant={"ghost"}
                size="icon"
                aria-label="Search"
                onClick={handleSearch}
              >
                <SearchIcon color="#70707E" size={30} />
              </Button>
              <Input
                placeholder="Search"
                className="font-p border-0 text-dark-gray shadow-none placeholder:text-dark-gray focus-visible:ring-0"
                value={input}
                data-slot="dropdown-menu-trigger"
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyPress}
                onFocus={() => {
                  setDropdownOpen(true);
                }}
                onBlur={(e) => {
                  if (!searchRef.current.contains(e.relatedTarget)) {
                    setDropdownOpen(false);
                  }
                }}
              />
              {input && (
                <Button
                  className="flex self-center hover:bg-opacity-10"
                  variant={"ghost"}
                  size="icon"
                  aria-label="Clear"
                  onClick={() => {
                    setDropdownOpen(false);
                    setInput("");
                    setFilter("searchQuery", "");
                    router.push(`/events`);
                  }}
                >
                  clear
                </Button>
              )}
            </div>
          </div>
          {dropdownOpen && (
            <div className="font-h1 relative text-black">
              <div className="absolute -top-2 left-0 w-full rounded-b-md bg-white bg-rainbow-diagonal p-[1px]">
                <div className="flex flex-col rounded-b-md bg-white p-5">
                  {[
                    "Research",
                    "Quantum",
                    "Open Source",
                    "Agentic AI",
                    "Cisco Live",
                    "AI/ML",
                    "Gen AI",
                  ].map((label) => (
                    <Button
                      key={label}
                      variant={"ghost"}
                      size="lg"
                      className="w-full"
                      aria-label={label}
                      onClick={() => {
                        setInput(label);
                        router.push(`/events?search=${label}`);
                        setFilter("searchQuery", label);
                        setDropdownOpen(false);
                      }}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
